/**
 * Keyword Demand Insights
 *
 * Shows market demand for the user's missing skills after ATS analysis.
 * Fetches /api/v1/stats/keywords, cross-references with missingSkills,
 * and surfaces top opportunities sorted by effectiveness score.
 *
 * Only renders when there are actionable insights (positive effectiveness,
 * at least 1 match). Returns null while loading or if no data.
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import type { KeywordDemandInsightsProps } from '../types';

interface KeywordEffectiveness {
  keyword: string;
  frequency: number;
  avgScoreWith: number;
  avgScoreWithout: number;
  effectiveness: number;
  confidence: 'high' | 'medium' | 'low';
  topRoles: string[];
}

interface KeywordEffectivenessResponse {
  keywords: KeywordEffectiveness[];
}

export const KeywordDemandInsights: React.FC<KeywordDemandInsightsProps> = ({
  missingSkills,
  backendUrl,
}) => {
  const [insights, setInsights] = useState<KeywordEffectiveness[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (missingSkills.length === 0) {
      setLoaded(true);
      return;
    }

    const controller = new AbortController();

    fetch(`${backendUrl}/api/v1/stats/keywords`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((body: { data: KeywordEffectivenessResponse }) => {
        const allKeywords: KeywordEffectiveness[] = body.data?.keywords ?? [];
        const missingLower = missingSkills.map((s) => s.toLowerCase().trim());

        const matched = allKeywords
          .filter((kw) =>
            missingLower.some(
              (m) => m.includes(kw.keyword) || kw.keyword.includes(m),
            ),
          )
          .filter((kw) => kw.effectiveness > 0)
          .sort((a, b) => b.effectiveness - a.effectiveness)
          .slice(0, 5);

        setInsights(matched);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));

    return () => controller.abort();
  }, [missingSkills, backendUrl]);

  if (!loaded || insights.length === 0) return null;

  return (
    <div className="ats-report-keyword-demand">
      <div className="ats-report-keyword-demand__header">
        <TrendingUp className="ats-report-keyword-demand__icon" />
        <span className="ats-report-keyword-demand__title">Market Demand</span>
        <span className="ats-report-keyword-demand__subtitle">
          Missing skills with proven score impact
        </span>
      </div>

      <div className="ats-report-keyword-demand__list">
        {insights.map((kw) => (
          <div key={kw.keyword} className="ats-report-keyword-demand__item">
            <span className="ats-report-keyword-demand__skill">{kw.keyword}</span>
            <div className="ats-report-keyword-demand__meta">
              <span
                className={`ats-report-keyword-demand__badge ats-report-keyword-demand__badge--${kw.effectiveness >= 5 ? 'high' : 'medium'}`}
              >
                +{kw.effectiveness} pts
              </span>
              {kw.confidence === 'high' && (
                <span className="ats-report-keyword-demand__confidence">
                  {kw.frequency} analyses
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

KeywordDemandInsights.displayName = 'KeywordDemandInsights';

export default KeywordDemandInsights;
